import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { formatDongDisplayName } from '../utils/dong';

interface Complex {
  id: number;
  code: number;
  name: string;
  alias?: string;
  address: string;
  complexNo: number;
  groupName?: string | null;
  _count: {
    buildings: number;
  };
  buildings: Array<{
    id: number;
    dongNumber: number;
    dongName?: string | null;
    _count: {
      units: number;
    };
  }>;
}

interface ComplexesResponse {
  message: string;
  data: Complex[];
  count: number;
}

interface CoverageUnit {
  id: number;
  code: string;
  floor: number;
  suffix: number;
  building: {
    id: number;
    dongNumber: number;
    dongName?: string | null;
  };
}

interface CoverageCard {
  id: number;
  name: string;
  createdAt: string;
  units: CoverageUnit[];
}

interface CoverageSummary {
  complexId: number;
  complexName: string;
  groupName: string | null;
  totalUnits: number;
  assignedUnits: number;
  unassignedUnits: number;
  coverageRate: number;
}

interface ComplexCardCoverageData {
  summary: CoverageSummary;
  cards: CoverageCard[];
  unassignedUnits: CoverageUnit[];
}

interface ComplexCardCoverageResponse {
  message: string;
  data: ComplexCardCoverageData;
}

type SortKey = 'name' | 'code' | 'complexNo' | 'buildings' | 'units';

const SORT_OPTIONS: Array<{ value: SortKey; label: string }> = [
  { value: 'name', label: '단지명' },
  { value: 'code', label: '코드' },
  { value: 'complexNo', label: '단지번호' },
  { value: 'buildings', label: '동 수' },
  { value: 'units', label: '세대 수' },
];

const UNGROUPED_KEY = '__UNGROUPED__';

const fetchComplexes = async (): Promise<ComplexesResponse> => {
  const response = await fetch('/api/complexes');
  if (!response.ok) {
    throw new Error('단지 정보를 가져오는데 실패했습니다');
  }
  return response.json();
};

const updateComplex = async (id: number, data: Partial<Complex>): Promise<Complex> => {
  const response = await fetch(`/api/complexes/${id}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || '단지 수정에 실패했습니다');
  }
  const result = await response.json();
  return result.data;
};

const deleteComplex = async (id: number): Promise<void> => {
  const response = await fetch(`/api/complexes/${id}`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || '단지 삭제에 실패했습니다');
  }
};

const bulkDeleteComplexes = async (ids: number[]): Promise<void> => {
  const response = await fetch('/api/complexes/bulk-delete', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ ids }),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || '단지 일괄 삭제에 실패했습니다');
  }
};

interface GroupUpdateResponse {
  message: string;
  data: Complex[];
  updatedCount: number;
}

const updateComplexGroup = async ({ ids, groupName }: { ids: number[]; groupName: string | null }): Promise<GroupUpdateResponse> => {
  const response = await fetch('/api/complexes/group', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ ids, groupName }),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || '단지 그룹 변경에 실패했습니다');
  }
  return response.json();
};

const fetchComplexCardCoverage = async (complexId: number): Promise<ComplexCardCoverageResponse> => {
  const response = await fetch(`/api/complexes/${complexId}/card-coverage`);
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || '카드 커버리지 정보를 가져오는데 실패했습니다');
  }
  return response.json();
};

const ComplexesPage: React.FC = () => {
  const [editingComplex, setEditingComplex] = useState<Complex | null>(null);
  const [deletingComplex, setDeletingComplex] = useState<Complex | null>(null);
  const [selectedComplexes, setSelectedComplexes] = useState<Set<number>>(new Set());
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '',
    alias: '',
    address: '',
    complexNo: 0,
    groupName: ''
  });
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);
  const [groupNameInput, setGroupNameInput] = useState('');
  const [coverageModal, setCoverageModal] = useState<{
    isOpen: boolean;
    complex: Complex | null;
    data: ComplexCardCoverageData | null;
    isLoading: boolean;
    error: string | null;
  }>({
    isOpen: false,
    complex: null,
    data: null,
    isLoading: false,
    error: null
  });

  const queryClient = useQueryClient();
  
  const { data, isLoading, error } = useQuery({
    queryKey: ['complexes'],
    queryFn: fetchComplexes,
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<Complex> }) => updateComplex(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['complexes'] });
      setEditingComplex(null);
      alert('단지가 성공적으로 수정되었습니다.');
    },
    onError: (error: Error) => {
      alert(`수정 실패: ${error.message}`);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteComplex,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['complexes'] });
      setDeletingComplex(null);
      alert('단지가 성공적으로 삭제되었습니다.');
    },
    onError: (error: Error) => {
      alert(`삭제 실패: ${error.message}`);
    },
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: bulkDeleteComplexes,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['complexes'] });
      setSelectedComplexes(new Set());
      setShowBulkDeleteConfirm(false);
      alert('선택된 단지들이 성공적으로 삭제되었습니다.');
    },
    onError: (error: Error) => {
      alert(`일괄 삭제 실패: ${error.message}`);
    },
  });

  const assignGroupMutation = useMutation({
    mutationFn: updateComplexGroup,
    onSuccess: response => {
      queryClient.invalidateQueries({ queryKey: ['complexes'] });
      setIsGroupModalOpen(false);
      setGroupNameInput('');
      setSelectedComplexes(new Set());
      alert(response.message);
    },
    onError: (error: Error) => {
      alert(`그룹 변경 실패: ${error.message}`);
    },
  });

  const handleEdit = (complex: Complex) => {
    setEditingComplex(complex);
    setEditForm({
      name: complex.name,
      alias: complex.alias || '',
      address: complex.address,
      complexNo: complex.complexNo,
      groupName: complex.groupName || ''
    });
  };

  const handleEditSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingComplex) return;
    
    const updateData: Partial<Complex> = {
      name: editForm.name,
      address: editForm.address,
      complexNo: editForm.complexNo
    };
    
    if (editForm.alias.trim()) {
      updateData.alias = editForm.alias;
    }
    if (editForm.groupName.trim()) {
      updateData.groupName = editForm.groupName.trim();
    } else {
      updateData.groupName = null;
    }
    
    updateMutation.mutate({
      id: editingComplex.id,
      data: updateData
    });
  };

  const handleDelete = (complex: Complex) => {
    setDeletingComplex(complex);
  };

  const confirmDelete = () => {
    if (!deletingComplex) return;
    deleteMutation.mutate(deletingComplex.id);
  };

  const handleComplexSelect = (complexId: number, checked: boolean) => {
    const newSelected = new Set(selectedComplexes);
    if (checked) {
      newSelected.add(complexId);
    } else {
      newSelected.delete(complexId);
    }
    setSelectedComplexes(newSelected);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked && data?.data) {
      setSelectedComplexes(new Set(data.data.map(c => c.id)));
    } else {
      setSelectedComplexes(new Set());
    }
  };

  const handleBulkDelete = () => {
    if (selectedComplexes.size === 0) return;
    setShowBulkDeleteConfirm(true);
  };

  const confirmBulkDelete = () => {
    const idsToDelete = Array.from(selectedComplexes);
    bulkDeleteMutation.mutate(idsToDelete);
  };

  const formatUnitLabel = (unit: CoverageUnit) => {
    const floorLabel = `${unit.floor}층`;
    const suffixLabel = unit.suffix.toString().padStart(2, '0');
    return `${formatDongDisplayName(unit.building.dongNumber, unit.building.dongName)} ${floorLabel} ${suffixLabel}호 (${unit.code})`;
  };

  const sortedComplexes = useMemo<(Complex & { totalUnits: number })[]>(() => {
    if (!data?.data) {
      return [];
    }

    const complexesWithTotals = data.data.map(complex => ({
      ...complex,
      totalUnits: complex.buildings.reduce(
        (sum, building) => sum + building._count.units,
        0
      ),
    }));

    const multiplier = sortDirection === 'asc' ? 1 : -1;

    return [...complexesWithTotals].sort((a, b) => {
      let comparison = 0;

      switch (sortKey) {
        case 'name':
          comparison = a.name.localeCompare(b.name, 'ko');
          break;
        case 'code':
          comparison = a.code - b.code;
          break;
        case 'complexNo':
          comparison = a.complexNo - b.complexNo;
          break;
        case 'buildings':
          comparison = a._count.buildings - b._count.buildings;
          break;
        case 'units':
          comparison = a.totalUnits - b.totalUnits;
          break;
        default:
          comparison = 0;
      }

      return comparison * multiplier;
    });
  }, [data?.data, sortDirection, sortKey]);

  const groupedComplexes = useMemo(
    () =>
      sortedComplexes.length === 0
        ? []
        : Array.from(
            sortedComplexes.reduce(
              (acc, complex) => {
                const key =
                  complex.groupName && complex.groupName.trim().length > 0
                    ? complex.groupName.trim()
                    : UNGROUPED_KEY;
                if (!acc.has(key)) {
                  acc.set(key, []);
                }
                acc.get(key)!.push(complex);
                return acc;
              },
              new Map<string, Array<Complex & { totalUnits: number }>>()
            ).entries()
          )
            .sort((a, b) => {
              if (a[0] === UNGROUPED_KEY && b[0] === UNGROUPED_KEY) return 0;
              if (a[0] === UNGROUPED_KEY) return 1;
              if (b[0] === UNGROUPED_KEY) return -1;
              return a[0].localeCompare(b[0], 'ko');
            })
            .map(([key, complexes]) => ({
              key,
              label: key === UNGROUPED_KEY ? '그룹 없음' : key,
              complexes,
            })),
    [sortedComplexes]
  );

  const existingGroupNames = useMemo(() => {
    if (!data?.data) {
      return [];
    }
    const names = new Set<string>();
    data.data.forEach(complex => {
      const name = complex.groupName?.trim();
      if (name) {
        names.add(name);
      }
    });
    return Array.from(names).sort((a, b) => a.localeCompare(b, 'ko'));
  }, [data?.data]);

  const selectedComplexIdsArray = useMemo(() => Array.from(selectedComplexes), [selectedComplexes]);

  const selectedComplexList = useMemo(
    () =>
      data?.data
        ? data.data.filter(complex => selectedComplexes.has(complex.id))
        : [],
    [data?.data, selectedComplexes]
  );

  const commonGroupName = useMemo(() => {
    if (selectedComplexList.length === 0) {
      return '';
    }
    const firstGroup = selectedComplexList[0]?.groupName?.trim() || '';
    const isUniform = selectedComplexList.every(
      complex => (complex.groupName?.trim() || '') === firstGroup
    );
    return isUniform ? firstGroup : '';
  }, [selectedComplexList]);

  const handleGroupToggle = (complexIds: number[]) => {
    setSelectedComplexes(prev => {
      const next = new Set(prev);
      const isFullySelected = complexIds.every(id => next.has(id));
      if (isFullySelected) {
        complexIds.forEach(id => next.delete(id));
      } else {
        complexIds.forEach(id => next.add(id));
      }
      return next;
    });
  };

  const openGroupModal = () => {
    if (selectedComplexes.size === 0) {
      alert('그룹으로 묶을 단지를 선택해주세요.');
      return;
    }
    setGroupNameInput(commonGroupName);
    setIsGroupModalOpen(true);
  };

  const handleGroupModalClose = () => {
    setIsGroupModalOpen(false);
    setGroupNameInput('');
  };

  const handleGroupModalSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (selectedComplexIdsArray.length === 0) {
      alert('그룹으로 묶을 단지를 선택해주세요.');
      return;
    }
    const trimmed = groupNameInput.trim();
    if (!trimmed) {
      alert('그룹 이름을 입력해주세요.');
      return;
    }
    assignGroupMutation.mutate({
      ids: selectedComplexIdsArray,
      groupName: trimmed
    });
  };

  const handleClearGroup = () => {
    if (selectedComplexIdsArray.length === 0) {
      alert('그룹을 해제할 단지를 선택해주세요.');
      return;
    }
    if (!window.confirm(`선택된 ${selectedComplexIdsArray.length}개 단지의 그룹을 해제하시겠습니까?`)) {
      return;
    }
    assignGroupMutation.mutate({
      ids: selectedComplexIdsArray,
      groupName: null
    });
  };

  const handleCoverageModalClose = () => {
    setCoverageModal({
      isOpen: false,
      complex: null,
      data: null,
      isLoading: false,
      error: null
    });
  };

  const handleCoverageModalOpen = async (complex: Complex) => {
    setCoverageModal({
      isOpen: true,
      complex,
      data: null,
      isLoading: true,
      error: null
    });

    try {
      const response = await fetchComplexCardCoverage(complex.id);
      setCoverageModal(prev => ({
        ...prev,
        data: response.data,
        isLoading: false
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : '카드 커버리지 정보를 불러올 수 없습니다.';
      setCoverageModal(prev => ({
        ...prev,
        error: message,
        isLoading: false
      }));
    }
  };

  if (error) {
    return (
      <div className="page-wide">
        <div className="card card-body text-center">
          <h1 className="page-title mb-2">오류</h1>
          <p className="text-red-600 text-sm">단지를 불러오는데 실패했습니다. 다시 시도해주세요.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-wide min-h-screen flex flex-col">
      <div className="card p-4 sm:p-6 flex flex-col flex-1">
        <h1 className="page-title mb-4">단지</h1>

        <div className="flex flex-col flex-1 min-h-0">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">단지 목록</h2>
            <div className="flex items-center gap-4 flex-wrap justify-end">
              {data && (
                <div className="flex items-center gap-3 text-sm text-gray-500">
                  <span>{data.count}개 단지</span>
                  {existingGroupNames.length > 0 && (
                    <span>{existingGroupNames.length}개 그룹</span>
                  )}
                </div>
              )}
              <div className="flex items-center gap-2">
                <label htmlFor="complex-sort-key" className="text-sm text-gray-500">
                  정렬
                </label>
                <select
                  id="complex-sort-key"
                  value={sortKey}
                  onChange={(event) => setSortKey(event.target.value as SortKey)}
                  className="text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {SORT_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'))}
                  className="text-sm px-2 py-1 border border-gray-300 rounded hover:bg-gray-100 transition-colors"
                >
                  {sortDirection === 'asc' ? '오름차순' : '내림차순'}
                </button>
              </div>
            </div>
          </div>

          {/* Bulk Actions */}
          {data && data?.data?.length > 0 && (
            <div className="flex items-center justify-between mb-4 p-3 bg-gray-50 rounded-lg shrink-0">
              <div className="flex items-center gap-3 flex-wrap">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={data?.data?.length > 0 && selectedComplexes.size === data?.data?.length}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  전체 선택
                </label>
                {selectedComplexes.size > 0 && (
                  <span className="text-sm text-gray-600">
                    {selectedComplexes.size}개 선택됨
                  </span>
                )}
              </div>
              {selectedComplexes.size > 0 && (
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    onClick={openGroupModal}
                    className="px-3 py-1 text-sm bg-indigo-500 text-white rounded hover:bg-indigo-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={assignGroupMutation.isPending}
                  >
                    {assignGroupMutation.isPending ? '처리 중...' : '그룹 지정'}
                  </button>
                  <button
                    onClick={handleClearGroup}
                    className="px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={assignGroupMutation.isPending}
                  >
                    그룹 해제
                  </button>
                  <button
                    onClick={handleBulkDelete}
                    className="px-3 py-1 text-sm bg-red-500 text-white rounded hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={bulkDeleteMutation.isPending}
                  >
                    {bulkDeleteMutation.isPending ? '삭제 중...' : '선택 삭제'}
                  </button>
                </div>
              )}
            </div>
          )}

          <div className="flex-1 overflow-y-auto pr-1">
            {isLoading ? (
              <div className="border rounded-lg p-4">
                <div className="animate-pulse">
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                </div>
              </div>
            ) : sortedComplexes.length === 0 ? (
              <div className="border rounded-lg p-4 text-center">
                <p className="text-gray-500">단지를 찾을 수 없습니다</p>
                <p className="text-sm text-gray-400 mt-1">
                  단지를 추가하려면 Excel 파일을 업로드하세요
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                {groupedComplexes.map(group => {
                  const groupComplexIds = group.complexes.map(complex => complex.id);
                  const isGroupSelected = groupComplexIds.every(id => selectedComplexes.has(id));
                  const isGroupPartiallySelected =
                    !isGroupSelected && groupComplexIds.some(id => selectedComplexes.has(id));

                  return (
                    <div key={group.key} className="border rounded-lg">
                      <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">
                            {group.label}
                          </h3>
                          <p className="text-sm text-gray-500">
                            {group.complexes.length}개 단지
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          {isGroupPartiallySelected && (
                            <span className="text-xs font-medium text-amber-500">
                              일부 선택됨
                            </span>
                          )}
                          <button
                            onClick={() => handleGroupToggle(groupComplexIds)}
                            className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 transition-colors"
                          >
                            {isGroupSelected ? '선택 해제' : '그룹 선택'}
                          </button>
                        </div>
                      </div>
                      <div className="space-y-3 p-4">
                        {group.complexes.map(complex => (
                          <div
                            key={complex.id}
                            className="border rounded-lg p-4 hover:bg-gray-50 hover:shadow-md transition-all"
                          >
                            <div className="flex items-start gap-3">
                              <input
                                type="checkbox"
                                checked={selectedComplexes.has(complex.id)}
                                onChange={(e) => handleComplexSelect(complex.id, e.target.checked)}
                                className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <h4 className="font-medium text-gray-900">
                                    {complex.name}
                                  </h4>
                                  {complex.alias && (
                                    <span className="text-xs text-gray-500">
                                      ({complex.alias})
                                    </span>
                                  )}
                                </div>
                                <p className="text-sm text-gray-600 mb-2">
                                  {complex.address}
                                </p>
                                <div className="flex items-center gap-4 text-xs text-gray-500">
                                  <span>코드: {complex.code}</span>
                                  <span>동: {complex._count.buildings}</span>
                                  <span>세대: {complex.totalUnits}</span>
                                  {complex.groupName && group.key === UNGROUPED_KEY && (
                                    <span className="text-indigo-600">
                                      그룹: {complex.groupName}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="flex flex-col gap-2">
                                <Link
                                  to={`/complexes/${complex.id}`}
                                  className="text-xs text-blue-600 font-medium hover:text-blue-800"
                                >
                                  자세히 보기 →
                                </Link>
                                <button
                                  onClick={() => handleCoverageModalOpen(complex)}
                                  className="px-2 py-1 text-xs bg-indigo-500 text-white rounded hover:bg-indigo-600 transition-colors"
                                >
                                  카드 커버리지
                                </button>
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => handleEdit(complex)}
                                    className="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                                  >
                                    수정
                                  </button>
                                  <button
                                    onClick={() => handleDelete(complex)}
                                    className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                                  >
                                    삭제
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Coverage Modal */}
      {coverageModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold">
                  {coverageModal.complex?.name} 카드 커버리지
                </h2>
                {coverageModal.complex?.groupName && (
                  <p className="text-sm text-indigo-600 mt-1">
                    그룹: {coverageModal.complex.groupName}
                  </p>
                )}
              </div>
              <button
                onClick={handleCoverageModalClose}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            {coverageModal.isLoading ? (
              <div className="border rounded-lg p-6 text-center">
                <p className="text-gray-600">카드 커버리지 정보를 불러오는 중입니다...</p>
              </div>
            ) : coverageModal.error ? (
              <div className="border rounded-lg p-6 text-center">
                <p className="text-red-500 mb-2">{coverageModal.error}</p>
                <button
                  onClick={() => {
                    if (coverageModal.complex) {
                      handleCoverageModalOpen(coverageModal.complex);
                    }
                  }}
                  className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                >
                  다시 시도
                </button>
              </div>
            ) : coverageModal.data ? (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="border rounded-lg p-4 bg-gray-50">
                    <p className="text-sm text-gray-500 mb-1">총 세대 수</p>
                    <p className="text-2xl font-semibold text-gray-900">
                      {coverageModal.data.summary.totalUnits}
                    </p>
                  </div>
                  <div className="border rounded-lg p-4 bg-gray-50">
                    <p className="text-sm text-gray-500 mb-1">카드에 배정된 세대</p>
                    <p className="text-2xl font-semibold text-emerald-600">
                      {coverageModal.data.summary.assignedUnits}
                    </p>
                  </div>
                  <div className="border rounded-lg p-4 bg-gray-50">
                    <p className="text-sm text-gray-500 mb-1">배정되지 않은 세대</p>
                    <p className={`text-2xl font-semibold ${coverageModal.data.summary.unassignedUnits > 0 ? 'text-red-500' : 'text-gray-900'}`}>
                      {coverageModal.data.summary.unassignedUnits}
                    </p>
                  </div>
                  <div className="border rounded-lg p-4 bg-gray-50">
                    <p className="text-sm text-gray-500 mb-1">커버리지</p>
                    <p className="text-2xl font-semibold text-indigo-600">
                      {coverageModal.data.summary.coverageRate.toFixed(1)}%
                    </p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">
                      카드별 세대 목록
                    </h3>
                    <span className="text-sm text-gray-500">
                      {coverageModal.data.cards.length}개 카드
                    </span>
                  </div>

                  {coverageModal.data.cards.length === 0 ? (
                    <div className="border rounded-lg p-4 text-center text-gray-500">
                      생성된 카드가 없습니다.
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {coverageModal.data.cards.map(card => (
                        <div key={card.id} className="border rounded-lg p-4">
                          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                            <div>
                              <h4 className="text-base font-semibold text-gray-900">
                                {card.name}
                              </h4>
                              <p className="text-xs text-gray-500">
                                생성일: {new Date(card.createdAt).toLocaleString('ko-KR')}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="px-2 py-1 text-xs bg-blue-50 text-blue-600 rounded-full">
                                {card.units.length}개 세대
                              </span>
                              <Link
                                to={`/cards/${card.id}`}
                                className="text-xs text-blue-600 hover:text-blue-800"
                              >
                                카드 상세 →
                              </Link>
                            </div>
                          </div>
                          {card.units.length === 0 ? (
                            <p className="text-sm text-gray-500">
                              이 카드에 속한 세대가 없습니다.
                            </p>
                          ) : (
                            <div className="flex flex-wrap gap-2">
                              {card.units.map(unit => (
                                <span
                                  key={unit.id}
                                  className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded"
                                >
                                  {formatUnitLabel(unit)}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">
                      카드에 속하지 않은 세대
                    </h3>
                    <span className="text-sm text-gray-500">
                      {coverageModal.data.unassignedUnits.length}개
                    </span>
                  </div>
                  {coverageModal.data.unassignedUnits.length === 0 ? (
                    <div className="border rounded-lg p-4 text-sm text-emerald-600">
                      모든 세대가 카드에 배정되어 있습니다.
                    </div>
                  ) : (
                    <div className="border rounded-lg p-4">
                      <div className="flex flex-wrap gap-2">
                        {coverageModal.data.unassignedUnits.map(unit => (
                          <span
                            key={`unassigned-${unit.id}`}
                            className="px-2 py-1 text-xs bg-red-50 text-red-600 rounded"
                          >
                            {formatUnitLabel(unit)}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      )}

      {/* Group Modal */}
      {isGroupModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h2 className="text-xl font-bold mb-4">그룹 지정</h2>
            <form onSubmit={handleGroupModalSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    그룹 이름
                  </label>
                  <input
                    type="text"
                    value={groupNameInput}
                    onChange={(e) => setGroupNameInput(e.target.value)}
                    placeholder="예: 서해 1차, 시범단지 등"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    autoFocus
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    선택된 {selectedComplexIdsArray.length}개 단지를 하나의 그룹으로 묶습니다.
                  </p>
                </div>
                {existingGroupNames.length > 0 && (
                  <div>
                    <p className="text-xs text-gray-600 mb-2">기존 그룹 빠르게 선택</p>
                    <div className="flex flex-wrap gap-2">
                      {existingGroupNames.map(name => (
                        <button
                          key={name}
                          type="button"
                          onClick={() => setGroupNameInput(name)}
                          className="px-2 py-1 text-xs border border-indigo-200 text-indigo-600 rounded hover:bg-indigo-50 transition-colors"
                        >
                          {name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={handleGroupModalClose}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
                >
                  취소
                </button>
                <button
                  type="submit"
                  disabled={assignGroupMutation.isPending}
                  className="px-4 py-2 bg-indigo-500 text-white rounded hover:bg-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {assignGroupMutation.isPending ? '저장 중...' : '저장'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingComplex && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h2 className="text-xl font-bold mb-4">단지 수정</h2>
            <form onSubmit={handleEditSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    단지명 *
                  </label>
                  <input
                    type="text"
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    별칭
                  </label>
                  <input
                    type="text"
                    value={editForm.alias}
                    onChange={(e) => setEditForm({ ...editForm, alias: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    그룹
                  </label>
                  <input
                    type="text"
                    value={editForm.groupName}
                    onChange={(e) => setEditForm({ ...editForm, groupName: e.target.value })}
                    placeholder="예: 서부권 1구역"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    주소 *
                  </label>
                  <input
                    type="text"
                    value={editForm.address}
                    onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    단지번호 *
                  </label>
                  <input
                    type="number"
                    value={editForm.complexNo}
                    onChange={(e) => setEditForm({ ...editForm, complexNo: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <button
                  type="button"
                  onClick={() => setEditingComplex(null)}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                  disabled={updateMutation.isPending}
                >
                  취소
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50"
                  disabled={updateMutation.isPending}
                >
                  {updateMutation.isPending ? '수정 중...' : '수정'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deletingComplex && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h2 className="text-xl font-bold mb-4">단지 삭제</h2>
            <p className="text-gray-600 mb-6">
              <strong>{deletingComplex.name}</strong> 단지를 삭제하시겠습니까?
              <br />
              <span className="text-sm text-red-600 mt-2 block">
                이 작업은 되돌릴 수 없습니다.
              </span>
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setDeletingComplex(null)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                disabled={deleteMutation.isPending}
              >
                취소
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 disabled:opacity-50"
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? '삭제 중...' : '삭제'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Modal */}
      {showBulkDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h2 className="text-xl font-bold mb-4">단지 일괄 삭제</h2>
            <p className="text-gray-600 mb-6">
              선택된 <strong>{selectedComplexes.size}개</strong>의 단지를 삭제하시겠습니까?
              <br />
              <span className="text-sm text-red-600 mt-2 block">
                이 작업은 되돌릴 수 없습니다.
              </span>
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowBulkDeleteConfirm(false)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
                disabled={bulkDeleteMutation.isPending}
              >
                취소
              </button>
              <button
                onClick={confirmBulkDelete}
                className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 disabled:opacity-50"
                disabled={bulkDeleteMutation.isPending}
              >
                {bulkDeleteMutation.isPending ? '삭제 중...' : '일괄 삭제'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComplexesPage;
